// Imports
import qs from "qs";
import jsonpack from "jsonpack";
import { PREFIX } from "api/";
import { logout } from "../lib/providers/auth-provider/actions";
import { markSessionExpired } from "./sessionExpiry";
import { rememberPrescriberAccessDenied } from "./prescriber";
import { ACCESS_DENIED_EVENT } from "./accessContext";

let loggingOutDisabledUser = false;

const PRESCRIBER_ACCESS_ERRORS = ["USER_DISABLED", "OWNER_DISABLED", "NO_COMPANY"];

/**
 * Whether a 401 means the session JWT is no longer valid.
 * @param {string} message API error message
 * @returns {boolean}
 */
export const isExpiredJwtMessage = (message) => {
  const normalized = String(message || "").toLowerCase();
  return (
    normalized === "jwt expired" ||
    normalized === "invalid signature" ||
    normalized.includes("jwt expired")
  );
};

/**
 * Reads a JSON error payload from fetch or socket responses without consuming a fetch body.
 * @param {object} result Fetch or socket Response
 * @returns {Promise<object>}
 */
const readErrorPayload = async (result) => {
  try {
    if (typeof result.clone === "function") {
      const text = await result.clone().text();
      if (text && typeof text === "object") {
        return text;
      }
      return text ? JSON.parse(text) : {};
    }
    if (result.body && typeof result.body === "object") {
      return result.body;
    }
  } catch (err) {
    return {};
  }
  return {};
};

/**
 * Returns the prescriber access error code from an API response, if any.
 * @param {Response} result - Fetch response to inspect.
 * @returns {Promise<string|null>} USER_DISABLED, NO_COMPANY, or null.
 */
const getPrescriberAccessError = async (result) => {
  if (result.status !== 403) return null;
  const parsed = await readErrorPayload(result);
  if (parsed && PRESCRIBER_ACCESS_ERRORS.includes(parsed.message)) {
    return parsed.message;
  }
  return null;
};

/**
 * Whether a 401 is an expired or invalid session JWT.
 * @param {object} result Fetch or socket Response
 * @returns {Promise<boolean>}
 */
const isExpiredSessionResponse = async (result) => {
  if (result.status !== 401) {
    return false;
  }
  const parsed = await readErrorPayload(result);
  return isExpiredJwtMessage(parsed && parsed.message);
};

/**
 * Copies the Redux session JWT onto the transport and request headers.
 * Login can leave the UI authenticated while the in-memory transport has no token.
 * @param {object} networkTransport Client transport
 * @param {object} store Redux store
 * @param {object} requestOptions Fetch options
 * @returns {object} Options with Authorization when a session token exists
 */
const withSessionAuth = (networkTransport, store, requestOptions) => {
  const authToken =
    store.getState().global &&
    store.getState().global.auth &&
    store.getState().global.auth.token;
  if (typeof authToken !== "string" || !authToken) {
    return requestOptions;
  }
  networkTransport.authenticate(authToken);
  if (requestOptions.headers && requestOptions.headers.Authorization) {
    return requestOptions;
  }
  return {
    ...requestOptions,
    headers: {
      ...(requestOptions.headers || {}),
      Authorization: `Bearer ${authToken}`,
    },
  };
};

const buildEnhancedFetch = networkTransport => async (
  path,
  options = {
    method: "GET",
    filters: {},
    include: [],
    orderBy: null,
    page: {},
    pageSize: {},
    limit: null,
    withGraphFetched: null,
    userType: "",
    questionnaireAnswerId: null
  },
  transport
) => {
  const query = { ...options.query };

  const {
    filters,
    include,
    orderBy,
    page,
    pageSize,
    limit,
    withGraphFetched,
    userType,
    ...other
  } = options;

  const store = require("../store/index").default;

  if (!path.includes("language"))
    query.language = store.getState().global.language.locale;

  if (userType) {
    query.userType = userType;
  }

  if (typeof filters === "object") {
    query.filters = filters;
  }

  if (include) {
    query.include = include;
  }

  if (page) {
    query.page = page;
  }

  if (pageSize) {
    query.pageSize = pageSize;
  }

  if (withGraphFetched) {
    query.withGraphFetched = withGraphFetched;
  }

  if (typeof orderBy === "string" && orderBy) {
    query.orderBy = orderBy;
  }
  
  if (limit) {
    query.limit = limit;
  }

  // Build URL
  let url;
  if (!path.includes("?"))
    url = `${PREFIX}${path}${qs.stringify(query, { addQueryPrefix: true })}`;
  else url = `${PREFIX}${path}`;

  const requestOptions = withSessionAuth(networkTransport, store, other);

  // Fetch
  const result = await networkTransport.fetch(url, requestOptions, transport);

  if (result.status === 429 && window.location.pathname !== "/429") {
    sessionStorage.setItem("lastPath", window.location.pathname)
    window.location.href = "/429";
    // Optionally, return here to prevent further processing
    return;
  }

  if (result.ok && loggingOutDisabledUser && path && path.includes("/user/login")) {
    loggingOutDisabledUser = false;
  }

  if (result.status === 403) {
    const deniedPayload = await readErrorPayload(result);
    if (deniedPayload && deniedPayload.message === "ACCESS_DENIED") {
      window.dispatchEvent(new CustomEvent(ACCESS_DENIED_EVENT));
    }
  }

  const accessError = await getPrescriberAccessError(result);
  const token =
    store.getState().global &&
    store.getState().global.auth &&
    store.getState().global.auth.token;

  if (!loggingOutDisabledUser && path && !path.includes("/user/logout")) {
    if (accessError) {
      loggingOutDisabledUser = true;
      rememberPrescriberAccessDenied(accessError);
      try {
        await store.dispatch(logout());
      } catch (e) {
        // session is cleared even if the logout request fails
      }
    } else if (
      token &&
      !path.includes("/user/login") &&
      !path.includes("/user/mfa/reenroll") &&
      !path.includes("/remote/login") &&
      (await isExpiredSessionResponse(result))
    ) {
      loggingOutDisabledUser = true;
      markSessionExpired();
      try {
        await store.dispatch(logout());
      } finally {
        loggingOutDisabledUser = false;
      }
    }
  }

  result.__proto__.json = async function(unpack = true) {
    return this.text().then(text => {
      try {
        return jsonpack.unpack(text);
      } catch (e) {
        return JSON.parse(text);
      }
    });
  };
  return result;
};

export default buildEnhancedFetch;
